import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Users, DollarSign, TrendingUp, FileText, Download, Search, Filter, X,
  BarChart3, ArrowUp, ArrowDown,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { RemitentesCharts } from '../components/analytics/RemitentesCharts';
import { RemitentesTabla } from '../components/analytics/RemitentesTabla';
import { ClientesDormidos } from '../components/analytics/ClientesDormidos';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const PERIODOS = [['7', '7 días'], ['30', '30 días'], ['90', '90 días'], ['', 'Todo']];

// El interceptor global de axios se registra al montar App: si esta pantalla se abre directo
// (F5 o link), las primeras llamadas pueden salir sin token y volver 403. Header explícito.
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const hoyISO = () => new Date().toISOString().slice(0, 10);
const haceDias = (n) => new Date(Date.now() - (n - 1) * 86400000).toISOString().slice(0, 10);

const formatMonto = (monto) => new Intl.NumberFormat('es-AR', {
  style: 'currency', currency: 'ARS', minimumFractionDigits: 2,
}).format(monto || 0);

const Delta = ({ valor }) => {
  if (valor === null || valor === undefined) return null;
  const sube = valor >= 0;
  const Icon = sube ? ArrowUp : ArrowDown;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${sube
      ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
      <Icon className="w-3 h-3" />{Math.abs(valor)}%
    </span>
  );
};

const Kpi = ({ icon: Icon, label, valor, delta, nota, acento, testid }) => (
  <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5"
    data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${acento}`}>
        <Icon className="w-4 h-4" />
      </div>
      <Delta valor={delta} />
    </div>
    <p className="text-xs text-gray-500 mb-1">{label}</p>
    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 tabular-nums">{valor}</p>
    {nota && <p className="text-[11px] text-gray-400 mt-1">{nota}</p>}
  </div>
);

export default function AnalyticsRemitentes() {
  const [loading, setLoading] = useState(true);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [exportando, setExportando] = useState(false);

  const [periodo, setPeriodo] = useState('30');
  const [fechaDesde, setFechaDesde] = useState(haceDias(30));
  const [fechaHasta, setFechaHasta] = useState(hoyISO());
  const [carteraId, setCarteraId] = useState('');
  const [clienteId, setClienteId] = useState('');
  const [destinatarioId, setDestinatarioId] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const [stats, setStats] = useState(null);
  const [agrupados, setAgrupados] = useState([]);
  const [totalRemitentes, setTotalRemitentes] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [carteras, setCarteras] = useState([]);
  const [clubes, setClubes] = useState([]);
  const [destinatarios, setDestinatarios] = useState([]);
  const [tab, setTab] = useState('remitentes');
  const [oscuro, setOscuro] = useState(() => document.documentElement.classList.contains('dark'));

  // Los gráficos necesitan saber el tema para los ejes: no se puede hacer con clases CSS
  useEffect(() => {
    const obs = new MutationObserver(() => setOscuro(document.documentElement.classList.contains('dark')));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (fechaDesde) p.append('fecha_desde', fechaDesde);
    if (fechaHasta) p.append('fecha_hasta', fechaHasta);
    if (carteraId) p.append('cartera_id', carteraId);
    if (clienteId) p.append('cliente_id', clienteId);
    if (destinatarioId) p.append('destinatario', destinatarioId);
    return p;
  }, [fechaDesde, fechaHasta, carteraId, clienteId, destinatarioId]);

  useEffect(() => {
    axios.get(`${API}/carteras`, auth()).then(r => setCarteras(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCarteras([]));
    axios.get(`${API}/analytics/destinatarios-unicos`, auth())
      .then(r => setDestinatarios(r.data?.destinatarios || [])).catch(() => setDestinatarios([]));
  }, []);

  // Los clubes del selector: los de la cartera elegida, o todos si no hay cartera
  useEffect(() => {
    const url = carteraId ? `${API}/clientes?cartera_id=${carteraId}` : `${API}/clientes`;
    axios.get(url, auth())
      .then(r => setClubes((Array.isArray(r.data) ? r.data : [])
        .map(c => ({ id: c.id, name: c.name, cartera: c.cartera_name }))
        .sort((a, b) => a.name.localeCompare(b.name))))
      .catch(() => setClubes([]));
  }, [carteraId]);

  // Debounce de la búsqueda: antes disparaba una consulta por cada letra
  useEffect(() => {
    const t = setTimeout(() => { setSearchQuery(searchInput); setCurrentPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/analytics/remitentes-stats?${params}`, auth())
      .then(r => { if (r.data?.success) setStats(r.data); })
      .catch(() => toast.error('Error al cargar estadísticas'))
      .finally(() => setLoading(false));
  }, [params]);

  const cargarRemitentes = useCallback(() => {
    setLoadingDetalle(true);
    const p = new URLSearchParams(params);
    if (searchQuery) p.append('search', searchQuery);
    p.append('page', currentPage);
    p.append('limit', 50);
    axios.get(`${API}/analytics/remitentes-agrupados?${p}`, auth())
      .then(r => {
        if (r.data?.success) {
          setAgrupados(r.data.remitentes);
          setTotalRemitentes(r.data.total);
          setTotalPages(r.data.pages);
        }
      })
      .catch(() => toast.error('Error al cargar los remitentes'))
      .finally(() => setLoadingDetalle(false));
  }, [params, searchQuery, currentPage]);

  useEffect(() => { cargarRemitentes(); }, [cargarRemitentes]);

  const aplicarPeriodo = (dias) => {
    setPeriodo(dias);
    setCurrentPage(1);
    if (dias) { setFechaDesde(haceDias(Number(dias))); setFechaHasta(hoyISO()); }
    else { setFechaDesde(''); setFechaHasta(''); }
  };

  const limpiarFiltros = () => {
    setCarteraId(''); setClienteId(''); setDestinatarioId(''); setSearchInput(''); setSearchQuery('');
    aplicarPeriodo('30');
  };

  const exportar = async () => {
    setExportando(true);
    try {
      const res = await axios.get(`${API}/analytics/export-remitentes-excel?${params}`, { ...auth(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `analisis_remitentes_${hoyISO()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel descargado');
    } catch (err) {
      toast.error('Error al exportar el Excel');
    } finally {
      setExportando(false);
    }
  };

  const k = stats?.kpis || {};
  const comp = stats?.comparativa;
  const inputCls = 'w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-violet-500';

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 space-y-5" data-testid="analytics-remitentes">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            <BarChart3 className="w-7 h-7 text-violet-600 dark:text-violet-400" />
            Análisis de Remitentes
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Quiénes envían los comprobantes, cuánto y a qué club
          </p>
        </div>
        <button onClick={exportar} disabled={exportando}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white text-sm font-medium transition-colors"
          data-testid="remitentes-export-btn">
          <Download className="w-4 h-4" />
          {exportando ? 'Generando…' : 'Exportar Excel'}
        </button>
      </div>

      <div className="flex items-center gap-1.5 border-b border-gray-200 dark:border-gray-700">
        {[['remitentes', 'Remitentes y montos'], ['dormidos', 'Clientes que dejaron de cargar']].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === k
              ? 'border-violet-600 text-violet-700 dark:text-violet-400'
              : 'border-transparent text-gray-500 hover:text-gray-800 dark:hover:text-gray-200'}`}
            data-testid={`tab-${k}`}>{label}</button>
        ))}
      </div>

      {tab === 'dormidos' ? (
        <ClientesDormidos carteraId={carteraId} clienteId={clienteId} carteras={carteras} clubes={clubes}
          onCartera={(v) => { setCarteraId(v); setClienteId(''); }} onClub={setClienteId}
          formatMonto={formatMonto} />
      ) : (
      <>

      {/* Filtros */}
      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-violet-600 dark:text-violet-400" />
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Filtros</h2>
          </div>
          <div className="flex items-center gap-1.5">
            {PERIODOS.map(([v, label]) => (
              <button key={label} onClick={() => aplicarPeriodo(v)}
                className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${periodo === v
                  ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                  : 'border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                data-testid={`periodo-${v || 'todo'}`}>{label}</button>
            ))}
            <button onClick={limpiarFiltros}
              className="ml-1 inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              data-testid="remitentes-limpiar">
              <X className="w-3 h-3" /> Limpiar
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1.5">Desde</label>
            <input type="date" value={fechaDesde} className={inputCls} data-testid="filtro-desde"
              onChange={(e) => { setPeriodo(null); setFechaDesde(e.target.value); }} />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1.5">Hasta</label>
            <input type="date" value={fechaHasta} className={inputCls} data-testid="filtro-hasta"
              onChange={(e) => { setPeriodo(null); setFechaHasta(e.target.value); }} />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1.5">Cartera</label>
            <select value={carteraId}
              onChange={(e) => { setCarteraId(e.target.value); setClienteId(''); setCurrentPage(1); }}
              className={inputCls} data-testid="filtro-cartera">
              <option value="">Todas las carteras</option>
              {carteras.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1.5">Club</label>
            <select value={clienteId} onChange={(e) => { setClienteId(e.target.value); setCurrentPage(1); }}
              className={inputCls} data-testid="filtro-club">
              <option value="">{carteraId ? 'Todos los clubes de la cartera' : 'Todos los clubes'}</option>
              {clubes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1.5">Destinatario</label>
            <select value={destinatarioId} onChange={(e) => { setDestinatarioId(e.target.value); setCurrentPage(1); }}
              className={inputCls} data-testid="filtro-destinatario">
              <option value="">Todos los destinatarios</option>
              {destinatarios.map((d, i) => <option key={i} value={d}>{d}</option>)}
            </select>
          </div>
        </div>
        {comp && (
          <p className="text-[11px] text-gray-500 mt-3" data-testid="comparativa-leyenda">
            Comparando contra el período anterior: {comp.desde} al {comp.hasta}
          </p>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
        <Kpi icon={DollarSign} label="Monto total recibido" valor={formatMonto(k.monto_total)}
          delta={comp?.var_monto} acento="bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300"
          nota={k.pagos_salientes?.cantidad
            ? `no incluye ${k.pagos_salientes.cantidad} pagos salientes por ${formatMonto(k.pagos_salientes.monto)}`
            : null}
          testid="kpi-monto" />
        <Kpi icon={Users} label="Remitentes únicos" valor={k.remitentes_unicos ?? 0}
          delta={comp?.var_remitentes} acento="bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
          testid="kpi-remitentes" />
        <Kpi icon={TrendingUp} label="Promedio por remitente" valor={formatMonto(k.promedio_por_remitente)}
          acento="bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300" testid="kpi-promedio" />
        <Kpi icon={FileText} label="Comprobantes con remitente" valor={k.comprobantes_totales ?? 0}
          delta={comp?.var_comprobantes}
          nota={k.comprobantes_sin_remitente ? `+${k.comprobantes_sin_remitente} sin remitente detectado` : null}
          acento="bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/40 dark:text-fuchsia-300"
          testid="kpi-comprobantes" />
      </div>

      {loading && !stats
        ? <div className="py-16 text-center text-gray-500" data-testid="remitentes-loading">Calculando…</div>
        : <RemitentesCharts serie={stats?.serie} granularidad={stats?.granularidad}
            topRemitentes={stats?.top_remitentes} distribucion={stats?.distribucion}
            oscuro={oscuro} formatMonto={formatMonto} />}

      <RemitentesTabla remitentes={agrupados} loading={loadingDetalle} total={totalRemitentes}
        page={currentPage} pages={totalPages} onPage={setCurrentPage} formatMonto={formatMonto}>
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" placeholder="Buscar por titular o CUIL…" value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className={`${inputCls} pl-9`} data-testid="remitentes-buscar" />
        </div>
      </RemitentesTabla>

      </>
      )}
    </div>
  );
}
